import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import QuickEditPopover from '../../components/Inventory/QuickEditPopover';
import ItemsDataGrid, {
  SORT_BY_COLUMN,
} from '../../components/Inventory/ItemsDataGrid';
import StatusFilter from '../../components/Inventory/StatusFilter';
import { confirm } from '../../components/MenuNew/ConfirmModal';
import {
  addInventoryStock,
  removeInventoryStock,
  adjustInventoryStock,
  updateProduct,
  deleteProduct,
  updateInventoryProduct,
  deleteInventoryProduct,
} from '../../redux/actions/inventory/inventory';
import { selectCurrentBusiness } from '../../redux/selectors';
import useMenuInventoryPages from 'hooks/useMenuInventoryPages';
import {
  ALL_INVENTORY_STATUSES,
  INVENTORY_SEARCH_DEBOUNCE_MS,
  isWithoutMenuItem,
  toInventoryStatuses,
} from 'utils/inventoryListQuery';

const InventoryItems = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // All statuses selected by default = show all items
  const [statusFilter, setStatusFilter] = useState(ALL_INVENTORY_STATUSES);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sortModel, setSortModel] = useState([]);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const currentBusiness = useSelector(selectCurrentBusiness);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, INVENTORY_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const sort = sortModel[0];

  const {
    data,
    loading: listLoading,
    error,
    refresh,
  } = useMenuInventoryPages({
    page,
    pageSize,
    categoryId: null,
    search: debouncedSearch || null,
    statuses: toInventoryStatuses(statusFilter),
    sortBy: sort ? SORT_BY_COLUMN[sort.field] : null,
    sortOrder: sort ? sort.sort : null,
    enabled: true,
  });

  const rows = data ? Object.values(data.products) : [];
  const loading = listLoading || actionLoading;

  useEffect(() => {
    if (error) console.error('Error fetching products:', error);
  }, [error]);

  // A deletion can leave the shown page past the last one.
  useEffect(() => {
    if (data && data.totalPages > 0 && page >= data.totalPages) {
      setPage(data.totalPages - 1);
    }
  }, [data, page]);

  // Keep the quick-edit item in sync with the refreshed page (after stock actions)
  useEffect(() => {
    if (!selectedItem || !data) return;
    const updatedItem = data.products[selectedItem._id];
    if (updatedItem && updatedItem !== selectedItem) {
      setSelectedItem(updatedItem);
    }
  }, [data, selectedItem]);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setPage(0);
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPage(0);
  };

  const handleEdit = (row) => {
    const sp = new URLSearchParams();
    sp.set('name', row.name);
    history.push({
      pathname: isWithoutMenuItem(row)
        ? `/inventory/items/inventory/${row.inventoryInfo.uuid}`
        : `/inventory/items/${row._id}`,
      search: sp.toString(),
    });
  };

  const handleDelete = async (row) => {
    const result = await confirm({
      title: t('Confirm Item Deletion'),
      message: t('Are you sure you want to delete this item?'),
      confirmText: t('delete'),
      cancelText: t('cancel'),
      background: '#E21508',
    });

    if (result === true) {
      try {
        setActionLoading(true);
        if (isWithoutMenuItem(row)) {
          await dispatch(deleteInventoryProduct(row.inventoryInfo.uuid));
        } else {
          await dispatch(deleteProduct(row._id));
        }
        refresh();
      } catch (error) {
        console.error('Error deleting item:', error);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleQuickView = (event, row) => {
    setSelectedItem(row);
    setPopoverAnchor(event.currentTarget);
  };

  const handleClosePopover = () => {
    setSelectedItem(null);
    setPopoverAnchor(null);
  };

  const handleSaveItem = async (updatedItem) => {
    try {
      setActionLoading(true);
      if (isWithoutMenuItem(updatedItem)) {
        // No menu item, so no price: only the inventory cost is saved.
        if (updatedItem.unitPrice !== undefined) {
          await dispatch(
            updateInventoryProduct(updatedItem.inventoryInfo.uuid, {
              unitPrice: updatedItem.unitPrice,
            })
          );
        }
        refresh();
        return;
      }
      // Prepare update data with price and unitPrice at top level
      const updateData = {
        price: updatedItem.price,
      };

      // Add unitPrice at top level if present
      if (updatedItem.unitPrice !== undefined) {
        updateData.unitPrice = updatedItem.unitPrice;
      } else if (updatedItem.inventoryInfo?.unitPrice !== undefined) {
        // Fallback for backward compatibility
        updateData.unitPrice = updatedItem.inventoryInfo.unitPrice;
      }

      // Use updateProduct for price and unitPrice updates
      await dispatch(updateProduct(updatedItem._id, updateData));
      refresh();
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStockAction = async (action, item, quantity) => {
    try {
      setActionLoading(true);
      const inventory = item.inventoryInfo || {};

      // Only include uuid, stock, and businessUuid fields
      const requestData = {
        uuid: inventory.uuid || item.uuid || '',
        stock: Number(quantity),
        businessUuid: currentBusiness?.uuid || '',
      };

      let apiCall;
      switch (action) {
        case 'add':
          apiCall = addInventoryStock(requestData);
          break;
        case 'remove':
          apiCall = removeInventoryStock(requestData);
          break;
        case 'adjust':
          apiCall = adjustInventoryStock(requestData);
          break;
        default:
          return;
      }

      await dispatch(apiCall);
      refresh();
    } catch (error) {
      console.error(`Error ${action}ing stock:`, error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddNew = () => {
    history.push('/inventory/items/new');
  };

  // Summary cards always describe the whole menu, not the filtered rows.
  const totalCount = data?.menuTotalCount ?? 0;
  const totalInventory = data?.totalInventory ?? 0;
  const totalCost = data?.totalCost ?? 0;
  const totalSales = data?.totalSales ?? 0;

  return (
    <Box sx={{ p: 2, backgroundColor: 'white', minHeight: '100vh' }}>
      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {[
          { label: t('Total Count'), value: Number(totalCount).toLocaleString() },
          { label: t('Total Inventory'), value: Number(totalInventory) % 1 !== 0 ? Number(totalInventory).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Number(totalInventory).toLocaleString() },
          {
            label: t('Total Cost'),
            value: `$${Number(totalCost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
          {
            label: t('Total Sales Value'),
            value: `$${Number(totalSales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          },
        ].map((stat) => (
          <Paper
            key={stat.label}
            variant="outlined"
            sx={{
              flex: 1,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {stat.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Search and Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
          <TextField
            size="small"
            placeholder={t('Search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <StatusFilter
            value={statusFilter}
            onChange={handleStatusFilterChange}
            disabled={loading}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleAddNew}
            startIcon={<AddIcon />}
            disabled={loading}
            sx={{ py: '7px' }}
          >
            {t('ADD NEW')}
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <ItemsDataGrid
        rows={rows}
        rowCount={data?.totalCount ?? 0}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onQuickView={handleQuickView}
      />

      {/* Quick Edit Popover */}
      <QuickEditPopover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        item={selectedItem}
        onSave={handleSaveItem}
        onStockAction={handleStockAction}
      />
    </Box>
  );
};

export default InventoryItems;
